import React from 'react';
import { motion } from 'framer-motion';
import { Tag, Plus, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { 
  useProjectTagsQuery, 
  useTagsQuery, 
  useProjectTagAssignmentMutations 
} from '@/hooks/content-generator/use-tags-query';

interface WorkbookTagsDropdownProps {
  pageId: string;
  onCreateTag?: () => void;
}

const WorkbookTagsDropdown: React.FC<WorkbookTagsDropdownProps> = ({
  pageId,
  onCreateTag,
}) => {
  const { data: assignedTags = [], isLoading: isLoadingAssigned } = useProjectTagsQuery(pageId);
  const { data: allTags = [], isLoading: isLoadingAll } = useTagsQuery();
  const { assignTag, removeTag, isPending } = useProjectTagAssignmentMutations();

  const assignedTagIds = new Set(assignedTags.map(tag => tag.id));
  const availableTags = allTags.filter(tag => !assignedTagIds.has(tag.id));

  const handleTagAssign = (tagId: string) => {
    if (isPending) return;
    assignTag({ tagId, projectId: pageId });
  };

  const handleTagRemove = (tagId: string) => {
    if (isPending) return;
    removeTag({ tagId, projectId: pageId });
  };

  const isLoading = isLoadingAssigned || isLoadingAll;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          className={cn(
            "h-8 w-8 flex items-center justify-center rounded-md border transition-colors",
            assignedTags.length > 0
              ? "text-purple-500 bg-purple-50 dark:bg-purple-950/30 border-purple-200 dark:border-purple-800/50 hover:bg-purple-100 dark:hover:bg-purple-950/50"
              : "text-purple-500 hover:bg-purple-50 dark:hover:bg-purple-950/30 border-transparent hover:border-purple-200 dark:hover:border-purple-800/50"
          )}
          data-test-id="categories-button"
          title="Manage categories"
        >
          <Tag size={16} />
        </motion.button>
      </DropdownMenuTrigger>
      
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel>Manage Categories</DropdownMenuLabel>
        
        {isLoading ? (
          <div className="p-2 space-y-2">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-full" />
          </div>
        ) : (
          <>
            {/* Assigned Tags Section */}
            {assignedTags.length > 0 && (
              <>
                <div className="px-2 py-1">
                  <div className="space-y-1">
                    {assignedTags.map(tag => (
                      <div 
                        key={tag.id}
                        className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors group"
                      >
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${tag.color} flex-shrink-0`} />
                          <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium text-white shadow-sm ${tag.color} transition-all duration-200`}>
                            {tag.name}
                          </span>
                        </div>
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => handleTagRemove(tag.id)}
                          disabled={isPending}
                          className="h-6 w-6 flex items-center justify-center rounded-full text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors opacity-0 group-hover:opacity-100"
                          title="Remove category"
                        >
                          <X size={12} />
                        </motion.button>
                      </div>
                    ))}
                  </div>
                </div>
                <DropdownMenuSeparator />
              </>
            )}

            {/* Available Tags Section */}
            {availableTags.length > 0 && (
              <>
                <div className="px-2 py-1">
                  <div className="space-y-1">
                    {availableTags.map(tag => (
                      <DropdownMenuItem
                        key={tag.id}
                        onClick={() => handleTagAssign(tag.id)}
                        disabled={isPending}
                        className="cursor-pointer p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${tag.color} flex-shrink-0`} />
                          <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium text-white shadow-sm ${tag.color} transition-all duration-200 hover:shadow-md`}>
                            {tag.name}
                          </span>
                        </div>
                      </DropdownMenuItem>
                    ))}
                  </div>
                </div>
                <DropdownMenuSeparator />
              </>
            )}

            {/* Empty State */}
            {assignedTags.length === 0 && availableTags.length === 0 && allTags.length === 0 && (
              <>
                <div className="px-2 py-4 text-center">
                  <div className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800 mb-2">
                    <Tag className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    No categories available
                  </p>
                </div>
                <DropdownMenuSeparator />
              </>
            )}

            {/* Create Category Button */}
            <DropdownMenuItem
              onClick={onCreateTag}
              className="cursor-pointer"
            >
              <Plus size={14} className="mr-2 text-green-500" />
              <span className="text-green-600 dark:text-green-400 font-medium">
                Create New Category
              </span>
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default WorkbookTagsDropdown; 